import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import type { PersonProfile } from "@sales-ops-amplify/shared";
import { ExternalLinksList } from "@/components/external-links-editor";

function RailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="min-w-0 space-y-2 border-b border-border pb-4 last:border-b-0">
      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      <div className="min-w-0">{children}</div>
    </section>
  );
}

export function PersonContactRail({
  profile,
  companyHref,
  onEnrich,
  enriching,
}: {
  profile: PersonProfile;
  companyHref?: string;
  onEnrich?: () => void;
  enriching?: boolean;
}) {
  const user = profile.user;
  const lists = profile.salesActivity?.lists ?? [];

  return (
    <aside className="min-w-0 max-w-full space-y-4 overflow-hidden rounded-lg border border-border bg-card p-4 text-sm">
      <RailSection title="Contact information">
        <div className="space-y-3">
          <div className="min-w-0">
            <p className="mb-1 text-[11px] font-medium text-muted-foreground">
              Emails
            </p>
            {profile.emails.length === 0 ? (
              <p className="text-muted-foreground">—</p>
            ) : (
              <ul className="space-y-2">
                {profile.emails.map((e) => (
                  <li key={e.email} className="min-w-0">
                    <a
                      href={`mailto:${e.email}`}
                      className="block break-all font-medium text-brand hover:underline"
                    >
                      {e.email}
                    </a>
                    <p className="break-words text-[11px] text-muted-foreground">
                      {[
                        e.primary ? "Primary" : null,
                        e.type,
                        e.source ? `Source: ${e.source}` : null,
                        e.status,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="min-w-0">
            <p className="mb-1 text-[11px] font-medium text-muted-foreground">
              Phone numbers
            </p>
            {profile.phones.length === 0 ? (
              <p className="text-muted-foreground">—</p>
            ) : (
              <ul className="space-y-1.5">
                {profile.phones.map((p) => (
                  <li key={`${p.type}-${p.number}`} className="min-w-0 break-words">
                    <a
                      href={`tel:${p.number}`}
                      className="break-all font-medium text-foreground hover:text-brand"
                    >
                      {p.number}
                    </a>
                    <span className="ml-1.5 text-[11px] text-muted-foreground">
                      {p.type}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {onEnrich ? (
            <button
              type="button"
              disabled={enriching}
              onClick={onEnrich}
              className="w-full rounded-md bg-brand px-3 py-2 text-xs font-semibold text-brand-foreground disabled:opacity-50"
            >
              {enriching ? "Enriching…" : "Enrich contact"}
            </button>
          ) : null}

          {profile.doNotCall ? (
            <p className="rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-2 text-[11px] text-amber-800 dark:text-amber-200">
              Do Not Call is enabled for this contact.
            </p>
          ) : null}
        </div>
      </RailSection>

      <RailSection title="About">
        <dl className="space-y-2">
          <div className="flex min-w-0 justify-between gap-3">
            <dt className="shrink-0 text-muted-foreground">Stage</dt>
            <dd className="min-w-0 break-words text-right font-medium">
              {profile.stage ?? "—"}
            </dd>
          </div>
          <div className="flex min-w-0 justify-between gap-3">
            <dt className="shrink-0 text-muted-foreground">Owner</dt>
            <dd className="min-w-0 break-words text-right font-medium">
              {profile.leadOwner || "—"}
            </dd>
          </div>
          <div className="flex min-w-0 justify-between gap-3">
            <dt className="shrink-0 text-muted-foreground">Lists</dt>
            <dd className="min-w-0 break-words text-right font-medium">
              {lists.length ? lists.join(", ") : "Not in any list"}
            </dd>
          </div>
          <div className="flex min-w-0 justify-between gap-3">
            <dt className="shrink-0 text-muted-foreground">Last activity</dt>
            <dd className="min-w-0 break-words text-right text-xs font-medium">
              {profile.lastInteractionAt
                ? new Date(profile.lastInteractionAt).toLocaleString()
                : "—"}
            </dd>
          </div>
          {profile.linkedinUrl ? (
            <div className="flex min-w-0 justify-between gap-3">
              <dt className="shrink-0 text-muted-foreground">Social</dt>
              <dd>
                <a
                  href={profile.linkedinUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand hover:underline"
                >
                  LinkedIn
                </a>
              </dd>
            </div>
          ) : null}
        </dl>
      </RailSection>

      <RailSection title="Account">
        {companyHref ? (
          <Link
            to={companyHref}
            className="block break-words font-medium text-brand hover:underline"
          >
            {profile.organization.name}
          </Link>
        ) : (
          <p className="break-words font-medium">{profile.organization.name}</p>
        )}
        {profile.organization.domain || profile.organization.websiteUrl ? (
          <a
            href={
              profile.organization.websiteUrl?.startsWith("http")
                ? profile.organization.websiteUrl
                : `https://${profile.organization.domain ?? profile.organization.websiteUrl}`
            }
            target="_blank"
            rel="noreferrer"
            className="mt-1 block break-all text-xs text-muted-foreground hover:text-brand"
          >
            {profile.organization.domain ?? profile.organization.websiteUrl}
          </a>
        ) : null}
        {profile.organization.industry ? (
          <p className="mt-1 break-words text-xs text-muted-foreground">
            {profile.organization.industry}
            {profile.organization.employeeCount
              ? ` · ${profile.organization.employeeCount.toLocaleString()} employees`
              : ""}
          </p>
        ) : null}
      </RailSection>

      <RailSection title="External links">
        <ExternalLinksList links={user?.externalLinks} />
      </RailSection>

      <RailSection title="Deals">
        {user?.opportunityIds?.length ? (
          <ul className="space-y-1">
            {user.opportunityIds.map((id) => (
              <li key={id} className="min-w-0">
                <Link
                  to={`/deals/${id}`}
                  className="break-all text-brand hover:underline"
                >
                  Deal {id.slice(0, 8)}…
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground">No deals associated</p>
        )}
      </RailSection>

      <RailSection title="Notes">
        {user?.notes?.trim() ? (
          <p className="whitespace-pre-wrap break-words text-muted-foreground">
            {user.notes}
          </p>
        ) : (
          <p className="text-muted-foreground">No notes yet</p>
        )}
      </RailSection>

      {profile.salesActivity ? (
        <RailSection title="Engagement">
          <dl className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <dt className="text-muted-foreground">Emails sent</dt>
              <dd className="font-semibold">
                {profile.salesActivity.emailSent ?? 0}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Opens</dt>
              <dd className="font-semibold">
                {profile.salesActivity.emailOpen ?? 0}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Replies</dt>
              <dd className="font-semibold">
                {profile.salesActivity.replied ?? 0}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Demos</dt>
              <dd className="font-semibold">
                {profile.salesActivity.demoed ?? 0}
              </dd>
            </div>
          </dl>
        </RailSection>
      ) : null}
    </aside>
  );
}
